import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, AlertCircle, MailCheck } from "lucide-react";
import api from "../lib/api";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bb-bg text-white flex items-center justify-center px-4 py-10 relative overflow-hidden">
      <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none"></div>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[500px] bg-gradient-to-br from-[#1c3df5]/20 to-transparent blur-3xl pointer-events-none"></div>
      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#3b59ff] to-[#1c3df5] flex items-center justify-center">
            <div className="w-3 h-3 rounded-full bg-white/95"></div>
          </div>
          <span className="font-display font-bold text-2xl tracking-tight">Northwind</span>
        </Link>
        <div className="bb-card rounded-3xl p-7 lg:p-8" data-testid="forgot-password-card">
          {sent ? (
            <div className="text-center" data-testid="forgot-password-success">
              <div className="w-12 h-12 mx-auto rounded-full bg-emerald-500/15 flex items-center justify-center mb-4">
                <MailCheck size={22} className="text-emerald-400" />
              </div>
              <h1 className="font-display font-bold text-[24px] text-white mb-2">Check your email</h1>
              <p className="text-[13px] text-white/55 leading-relaxed">
                If an account exists for <span className="text-white/80">{email}</span>, we've sent a
                secure link to reset your password. The link expires in 60 minutes.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center justify-center gap-2 mt-6 h-11 px-5 rounded-xl bb-blue text-white font-medium text-[14px]"
                data-testid="back-to-login-btn"
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-display font-bold text-[26px] text-white mb-1">Forgot password?</h1>
              <p className="text-[13px] text-white/55 mb-6">
                Enter your email and we'll send you a link to reset it.
              </p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="text-[12px] text-white/55 mb-1.5 block">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    placeholder="you@example.com"
                    data-testid="forgot-email-input"
                    className="w-full h-11 px-4 bg-white/[0.02] border border-white/10 rounded-xl text-[14px] text-white outline-none focus:border-[#1c3df5] placeholder:text-white/30"
                  />
                </div>
                {err && (
                  <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-rose-500/10 border border-rose-500/30 text-[12px] text-rose-300">
                    <AlertCircle size={14} className="shrink-0 mt-0.5" />
                    <span>{err}</span>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="forgot-submit-btn"
                  className="w-full h-11 rounded-xl bb-blue text-white font-medium text-[14px] inline-flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {loading ? "Sending..." : (<>Send reset link <ArrowRight size={15} /></>)}
                </button>
              </form>
              <div className="mt-6 pt-5 border-t border-white/5 text-center text-[13px] text-white/55">
                Remember it?{" "}
                <Link to="/login" className="text-[#7d8eff] hover:underline font-medium">
                  Sign in
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
